import { useState } from "react";
import { Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const labelCls = "mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#5C5C5C]";
const inputCls =
  "w-full rounded-xl border border-line bg-cream/50 px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-rose";

function PageForm({ page, onClose, onSaved }) {
  const isEdit = Boolean(page);
  const [form, setForm] = useState({
    title: page?.title || "",
    slug: page?.slug || "",
    content: page?.content || "",
    published: page ? page.published : true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return setError("Page title is required.");
    setSaving(true);
    setError("");
    try {
      if (isEdit) await api.patch(`/admin/pages/${page.id}`, form);
      else await api.post("/admin/pages", form);
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError((Array.isArray(detail) ? detail[0]?.msg : detail) || "Could not save the page.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center md:items-center md:p-8">
      <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div data-testid="page-form" role="dialog" aria-modal="true" className="relative flex max-h-[92vh] w-full max-w-2xl flex-col rounded-t-3xl bg-white md:rounded-3xl">
        <div className="flex items-center justify-between border-b border-line p-6 md:px-8">
          <h3 className="font-serif text-2xl font-medium">{isEdit ? "Edit Page" : "Add Page"}</h3>
          <button data-testid="page-form-close" onClick={onClose} aria-label="Close" className="flex h-10 w-10 items-center justify-center rounded-full border border-line transition-colors hover:bg-ink hover:text-cream">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={save} className="flex flex-col gap-5 overflow-y-auto p-6 md:p-8">
          <div>
            <label className={labelCls}>Page Title</label>
            <input data-testid="page-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>URL Slug {isEdit && "(locked to keep links working)"}</label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#5C5C5C]">/p/</span>
              <input
                data-testid="page-slug-input"
                value={form.slug}
                disabled={isEdit}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") }))}
                placeholder="my-page-url"
                className={`${inputCls} flex-1 disabled:opacity-60`}
              />
            </div>
          </div>
          <div>
            <label className={labelCls}>Content (blank line = new paragraph)</label>
            <textarea data-testid="page-content-input" rows={10} value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} className={`${inputCls} resize-none`} />
          </div>
          <label className="flex cursor-pointer items-center gap-3 text-sm font-medium">
            <input data-testid="page-published-toggle" type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} className="h-4 w-4 accent-[#C48B81]" />
            Published (visible on the website)
          </label>
          {error && <p data-testid="page-form-error" className="rounded-xl bg-red-50 px-4 py-3 text-xs font-medium text-red-600">{error}</p>}
          <button data-testid="page-save-button" type="submit" disabled={saving} className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-6 py-4 text-sm font-medium text-cream transition-colors hover:bg-rose hover:text-white disabled:opacity-60">
            {saving ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : isEdit ? "Save Page" : "Create Page"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function AdminPages({ pages, onChanged }) {
  const [editing, setEditing] = useState(null);

  const toggle = async (p) => {
    await api.patch(`/admin/pages/${p.id}`, { published: !p.published });
    onChanged();
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete the page "${p.title}"?`)) return;
    try {
      await api.delete(`/admin/pages/${p.id}`);
      onChanged();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error((Array.isArray(detail) ? detail[0]?.msg : detail) || "This page cannot be deleted.");
    }
  };

  return (
    <div data-testid="pages-manager">
      <div className="mb-5 flex items-center justify-between">
        <p className="text-sm text-[#5C5C5C]">Pages live at <span className="font-mono text-xs">/p/slug</span> — link them from Quick Links or Navigation.</p>
        <button data-testid="add-page-button" onClick={() => setEditing("new")} className="inline-flex shrink-0 items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-medium text-cream transition-colors hover:bg-rose hover:text-white">
          <Plus size={15} /> Add Page
        </button>
      </div>
      <div className="flex flex-col gap-3">
        {pages.map((p) => (
          <div key={p.id} data-testid={`page-row-${p.id}`} className={`flex flex-wrap items-center gap-3 rounded-2xl border border-line bg-white p-4 ${!p.published ? "opacity-60" : ""}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate font-serif text-lg font-medium">{p.title}</p>
              <a href={`/p/${p.slug}`} target="_blank" rel="noopener noreferrer" className="font-mono text-[11px] text-rose hover:underline">/p/{p.slug}</a>
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-xs font-medium">
              <input data-testid={`page-published-${p.id}`} type="checkbox" checked={p.published} onChange={() => toggle(p)} className="h-4 w-4 accent-[#C48B81]" />
              Published
            </label>
            <button data-testid={`page-edit-${p.id}`} onClick={() => setEditing(p)} className="inline-flex items-center gap-1.5 rounded-full border border-ink px-4 py-2 text-xs font-medium transition-colors hover:bg-ink hover:text-cream">
              <Pencil size={12} /> Edit
            </button>
            {p.is_system ? (
              <span className="w-9" title="Policy page — unpublish instead of deleting" />
            ) : (
              <button data-testid={`page-delete-${p.id}`} onClick={() => remove(p)} aria-label={`Delete ${p.title}`} className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-[#5C5C5C] transition-colors hover:border-red-400 hover:text-red-500">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
      {editing !== null && (
        <PageForm page={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />
      )}
    </div>
  );
}
